import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { ioTsRequired } from '../../shared/helpers/io-ts-helpers';
import { Action, AlphaTableType, ImmutableInterface } from '../../types/';

export interface AlphasAction<T = any> {
  selectedColumns?: Immutable.List<AlphaColumn>;
  results?: any;
  json?: T;
  alphaId?: string;
  date?: any;
  schema: any;
  records: any;
  pnl: any;
  before: any;
  after: any;
  errorMessage?: string;
  name?: string;
  alphaDetailPath?: string[];
  isTestPeriodShown?: string;
}

export interface BaseAlphasStore {
  alphas: AlphasStoreElement;
}
export type AlphasStore = ImmutableInterface<BaseAlphasStore>;

export type AlphaColumnBase = {
  id: string;
  name: string;
  active: boolean;
  category: 'Summary' | 'Settings' | 'Performance' | 'Properties';
  width?: number;
  parent?: 'settings' | 'is' | 'os' | 'selected';
  filterOperator?: '=' | '~';
  readOnly?: boolean;
  iconColumn?: boolean;
  possibleParents?: Array<'is' | 'os'>;
  display: boolean;
  type: string;
  tab?: string[];
  // Properties on the root of the alpha but are specific to a tab
  activeTabsWithoutParent?: Array<AlphaTableType>;
};

export type AlphaColumn = ImmutableInterface<AlphaColumnBase>;

interface BaseAlphasStoreElement {
  myAlphas: t.TypeOf<typeof AlphasListingData>;
  alphaDetail: any;
  fetchingData: boolean;
  showDetail: boolean;
  options: any;
  loadedOptions: boolean;
  detailTabSelected: number;
  selectedColumns: Immutable.List<AlphaColumn>;
  fetchingCheck: boolean;
  fetchingRecordSets: boolean;
  fetchingRecordSetsChart: boolean;
  fetchingRecordSetsSummary: boolean;
  recordsSets: any;
  chartData: any;
  fetchingSubmitAlpha: boolean;
  type: AlphaTableType;
  numOfUnsubmitted: number;
  numOfActive: number;
  numOfDecommissioned: number;
  fetchNum: boolean | null;
  fetchOSNum: boolean;
  fetchProdNum: boolean;
  hasComeFromAlphaPage: boolean;
  pageSizeUI: number;
  chartType: string;
  controller: any;
  alphasPath: string;
  selfCorrelationData: any;
  fetchingSelfCorrelation: boolean;
  hideActive: boolean;
  allActive: boolean;
  summaryTable: any;
  summaryOSPROD: any;
  currentAlphaId: string | null;
  fetchingAlphaSelections: boolean;
  superController: any;
  alphaSelections: any;
  fetchSuperAlpha: boolean;
  superAlphaProperties: {
    name: string;
  };
}
export type AlphasStoreElement = ImmutableInterface<BaseAlphasStoreElement>;

export interface AlphasCorrelationFailureAction {
  statusCode?: number;
}

export type AlphasCorrelationFailureActionType = {
  type: string;
  payload?: AlphasCorrelationFailureAction;
};

export type AlphasActionType<T = any> = Action<AlphasAction<T>>;

export const ParametersOptional = t.partial({
  limit: t.any,
  value: t.any,
});

export const ParametersMandatory = t.interface({
  name: t.string,
  result: t.string,
});

export const Parameters = t.intersection([ParametersMandatory, ParametersOptional]);

export const Checks = t.interface({
  checks: t.array(Parameters),
});

export const Records = t.interface({
  records: t.array(t.array(t.union([t.string, t.number, t.null]))),
});

export const Properties = t.interface({
  type: t.string,
  name: t.string,
  title: t.string,
});
export const CompetitionsProps = t.interface({
  id: t.string,
  name: t.string,
});

export const Schema = t.interface({
  schema: t.interface({
    properties: t.array(Properties),
  }),
});

export const Details = t.partial({
  details: t.intersection([Records, Schema]),
});

const Settings = t.interface({
  region: ioTsRequired,
  universe: ioTsRequired,
  delay: t.Integer,
  decay: t.Integer,
  neutralization: ioTsRequired,
  truncation: t.number,
  language: ioTsRequired,
  pasteurization: t.union([t.string, t.undefined]),
  unitHandling: t.union([t.string, t.undefined]),
  nanHandling: t.union([t.string, t.undefined]),
  testPeriod: t.union([t.string, t.undefined]),
  maxTrade: t.union([t.string, t.undefined]),
  maxPosition: t.union([t.string, t.undefined]),
});

export const AlphasDataResults = t.interface({
  category: t.union([t.string, t.null]),
  code: t.union([t.string, t.undefined]),
  color: t.union([t.string, t.null]),
  competitions: t.union([t.array(CompetitionsProps), t.null]),
  dateCreated: t.string,
  description: t.union([t.string, t.null, t.undefined]),
  favorite: t.union([t.boolean, t.null]),
  grade: t.union([t.string, t.null, t.undefined]),
  hidden: t.union([t.boolean, t.null]),
  id: t.string,
  is: t.intersection([Checks, Details]),
  name: t.union([t.string, t.null]),
  settings: Settings,
  stage: t.union([t.string, t.undefined]),
  status: t.string,
  tags: t.array(t.union([t.string, t.null])),
});

export const AlphasListingData = t.interface({
  count: t.Integer,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null]),
  results: t.array(AlphasDataResults),
});

const CorrelationProperties = t.interface({
  type: t.string,
  name: t.string,
  title: t.string,
});

const CorrelationSchema = t.interface({
  title: t.string,
  properties: t.array(CorrelationProperties),
  name: t.string,
});

export const CorrelationSelf = t.interface({
  records: t.array(t.array(t.union([t.string, t.null, t.number]))),
  schema: CorrelationSchema,
});

export type CorrelationSelfType = t.TypeOf<typeof CorrelationSelf>;

export const CorrelationProd = t.interface({
  records: t.array(t.array(t.number)),
  schema: CorrelationSchema,
});

export const RecordsSets = t.interface({
  count: t.number,
  next: t.union([t.string, t.null]),
  previous: t.union([t.string, t.null]),
  results: t.array(
    t.interface({
      name: t.string,
      title: t.string,
    })
  ),
});

export const ChartData = t.interface({
  records: t.array(t.union([t.string, t.null, t.number, t.array(t.union([t.string, t.number, t.null]))])),
  schema: CorrelationSchema,
});

const Value = t.interface({
  value: t.union([t.string, t.number]),
  label: t.string,
});

const PartialOption = t.partial({
  minValue: t.number,
  maxValue: t.number,
  choices: t.union([t.array(Value), t.null]),
});

const Option = t.interface({
  type: t.string,
});

const Choices = t.intersection([PartialOption, Option]);

const SettingsOptions = t.interface({
  decay: Choices,
  language: Choices,
  truncation: Choices,
});

export const AlphasOptions = t.interface({
  actions: t.interface({
    GET: t.interface({
      color: Choices,
      competitions: Choices,
      grade: Choices,
      settings: t.interface({
        children: SettingsOptions,
      }),
    }),
  }),
});
